/**
 * Font-inlining helper for html2canvas-based PDF exports.
 *
 * html2canvas 1.4.1 clones the target DOM into a detached rendering context.
 * In that context, external @font-face rules (loaded via a plain <link> to
 * /fonts.css) are not reliably re-linked, so text falls back to a system
 * font. Because our layout is measured against Manrope's ascent/descent,
 * that fallback pushes glyphs outside their container and clips them.
 *
 * The bullet-proof fix is to hand html2canvas the font bytes it needs. We
 * fetch each woff2 once, convert it to a base64 data URI, and inject a
 * <style> tag with a data-URI @font-face block into the cloned document via
 * the exporter's html2canvas `onclone` callback.
 */

// Weights and styles we actually use in the compliance dossier + certificate.
// Kept in sync with the @font-face rules in /public/fonts.css.
const FONT_MANIFEST = [
  { family: "Inter",             weight: 600, style: "normal", file: "inter-600-normal-latin.woff2" },
  { family: "JetBrains Mono",    weight: 400, style: "normal", file: "jetbrains-mono-400-normal-latin.woff2" },
  { family: "JetBrains Mono",    weight: 500, style: "normal", file: "jetbrains-mono-500-normal-latin.woff2" },
  { family: "JetBrains Mono",    weight: 600, style: "normal", file: "jetbrains-mono-600-normal-latin.woff2" },
  { family: "Manrope",           weight: 300, style: "normal", file: "manrope-300-normal-latin.woff2" },
  { family: "Manrope",           weight: 400, style: "normal", file: "manrope-400-normal-latin.woff2" },
  { family: "Manrope",           weight: 500, style: "normal", file: "manrope-500-normal-latin.woff2" },
  { family: "Manrope",           weight: 600, style: "normal", file: "manrope-600-normal-latin.woff2" },
  { family: "Manrope",           weight: 700, style: "normal", file: "manrope-700-normal-latin.woff2" },
  { family: "Manrope",           weight: 800, style: "normal", file: "manrope-800-normal-latin.woff2" },
  { family: "Playfair Display",  weight: 600, style: "normal", file: "playfair-display-600-normal-latin.woff2" },
  { family: "Playfair Display",  weight: 600, style: "italic", file: "playfair-display-600-italic-latin.woff2" },
  { family: "Playfair Display",  weight: 700, style: "normal", file: "playfair-display-700-normal-latin.woff2" },
];

let cachedCssPromise = null;

async function fileToDataUri(file) {
  const res = await fetch(`/fonts/${file}`, { cache: "force-cache" });
  if (!res.ok) throw new Error(`Font fetch failed: ${file} (${res.status})`);
  const buf = await res.arrayBuffer();
  // btoa on a binary string is memory-cheap and fine at these font sizes (<40 KB).
  let binary = "";
  const bytes = new Uint8Array(buf);
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
  return `data:font/woff2;base64,${btoa(binary)}`;
}

/**
 * Guarantees every declared face is loaded in the *live* document before
 * anything measures text.
 *
 * getInlinedFontsCss() below fixes the html2canvas clone. It does not fix the
 * measurement pass: both exporters mount the component and let it measure
 * itself - CertificateA4's FitText bakes absolute font sizes in a layout
 * effect, and the dossier decides its page breaks from measured heights -
 * BEFORE awaiting document.fonts.ready. On a warm cache the faces are already
 * there and nothing is wrong. On a cold cache (a browser that has never
 * loaded the site) the measurement runs against fallback metrics, which are
 * dimensionally unlike Manrope and Playfair Display, so glyphs are sized to
 * the wrong box and page breaks land in the wrong place: text clipped, and a
 * PDF that does not match the screen.
 *
 * Await this before mounting anything that measures itself.
 */
export async function ensureFontsLoaded() {
  if (!document.fonts || !document.fonts.load) return;
  try {
    await Promise.all(
      FONT_MANIFEST.map((f) =>
        document.fonts.load(`${f.style} ${f.weight} 100px '${f.family}'`)
      )
    );
    await document.fonts.ready;
  } catch (_) {
    /* An export must never be blocked by font loading. */
  }
}

/**
 * Fetches every declared font file, converts it to a data URI, and returns
 * a CSS string of @font-face rules ready to be dropped into a <style> tag.
 * The result is memoised for the lifetime of the tab.
 */
export function getInlinedFontsCss() {
  if (cachedCssPromise) return cachedCssPromise;
  cachedCssPromise = (async () => {
    const chunks = await Promise.all(
      FONT_MANIFEST.map(async (f) => {
        const uri = await fileToDataUri(f.file);
        return `@font-face { font-family: '${f.family}'; font-style: ${f.style}; font-weight: ${f.weight}; font-display: block; src: url(${uri}) format('woff2'); }`;
      })
    );
    return chunks.join("\n");
  })().catch((err) => {
    // Reset so a later retry can attempt again; return an empty string so
    // the export still proceeds (falling back to whatever the clone has).
    cachedCssPromise = null;
    // eslint-disable-next-line no-console
    console.warn("[inlineFonts] failed to inline fonts:", err);
    return "";
  });
  return cachedCssPromise;
}

/**
 * onclone helper: injects the inlined @font-face block into the cloned
 * document so html2canvas measures + paints against real Manrope/Inter/
 * Playfair/JetBrains glyphs instead of a system fallback.
 */
export function injectInlinedFonts(clonedDoc, css) {
  if (!css) return;
  const style = clonedDoc.createElement("style");
  style.setAttribute("data-inlined-fonts", "1");
  style.textContent = css;
  clonedDoc.head.appendChild(style);
}
